// frontend/src/components/Common/MasterDataManager.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import DataTable from './DataTable';
import Pagination from './Pagination';
import MasterDataModal from './MasterDataModal';
import './MasterDataManager.css';

const MasterDataManager = ({
  title,
  subtitle,
  createButtonLabel,
  api,
  columns,
  formFields,
  idField,
  displayField
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.getAll();
      
      if (response.data.success) {
        setData(response.data.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t('messages.error_load', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const displayValue = item[displayField];
    if (!window.confirm(t('messages.confirm_delete', `Are you sure you want to delete "${displayValue}"?`))) {
      return;
    }
    
    try {
      const response = await api.delete(item[idField]);
      if (response.data.success) {
        alert(t('messages.success_delete', 'Deleted successfully'));
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_delete', 'Failed to delete'));
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedItem(null);
    if (shouldReload) {
      loadData();
      setCurrentPage(1);
    }
  };

  // Client-side filtering
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return Object.values(item).some(value => 
      value?.toString().toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Build table columns with action buttons
  const tableColumns = [
    ...columns,
    {
      header: t('actions', 'Actions'),
      field: 'actions',
      sortable: false,
      render: (row) => (
        <div className="action-buttons">
          <button 
            onClick={() => handleEdit(row)}
            className="btn-action btn-edit"
            title={t('edit', 'Edit')}
          >
            <FiEdit2 size={16} />
          </button>
          <button 
            onClick={() => handleDelete(row)}
            className="btn-action btn-delete"
            title={t('delete', 'Delete')}
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="master-data-page">
      <div className="master-data-header">
        <div>
          <h2>{t(title)}</h2>
          <p className="master-data-subtitle">{t(subtitle)}</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t(createButtonLabel)}
        </button>
      </div>

      <div className="master-data-filters">
        <input
          type="text"
          placeholder={t('search.placeholder', 'Search...')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="search-input"
        />
        <span className="result-count">
          {filteredData.length} {t('records_found', 'records found')}
        </span>
      </div>

      <DataTable
        columns={tableColumns}
        data={paginatedData}
        loading={loading}
        error={error}
        onRetry={loadData}
        emptyMessage={t('no_data', 'No data available')}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {showModal && (
        <MasterDataModal
          title={selectedItem ? t('edit_item', 'Edit') : t('create_item', 'Create')}
          item={selectedItem}
          fields={formFields}
          idField={idField}
          api={api}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default MasterDataManager;